import { useEffect, useRef, useState } from "react";

const GLYPHS = "▓▒░█▌▐│┤╣║╗╝╔╚╠═╬◆◇◈◉◎●○★✦✧⊹∴∵≡≈⌬⌖⌗§¶†‡µƒ◊⟁⟁⌘⌘λΨΞΣΩΔΘΦΛϟ░░██∎".split("");

/**
 * Futuristic decode-on-mount text effect.
 * Each character starts as a random glyph and "settles" to the target character.
 * Characters lock in left-to-right with a smooth stagger.
 *
 * Props:
 *  - children: target string
 *  - delay (ms): when this line starts decoding
 *  - perChar (ms): how long each character takes to lock
 *  - className: applied to every span
 *  - testid
 */
export default function ScrambleText({
  children,
  delay = 0,
  perChar = 50,
  className = "",
  testid,
}) {
  const target = String(children);
  const chars = Array.from(target); // unicode safe
  const [out, setOut] = useState(chars.map((c) => (c === " " ? " " : "")));
  const frame = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const start = performance.now() + delay;
    const totalLock = chars.length * perChar + 350;

    const tick = (now) => {
      if (cancelled) return;
      const t = now - start;
      if (t < 0) {
        frame.current = requestAnimationFrame(tick);
        return;
      }
      const next = chars.map((real, i) => {
        if (real === " ") return " ";
        const lockAt = i * perChar + 220; // when this char locks in
        if (t >= lockAt) return real;
        // Still scrambling
        return GLYPHS[(Math.floor(t / 35) + i * 7) % GLYPHS.length];
      });
      setOut(next);
      if (t < totalLock) {
        frame.current = requestAnimationFrame(tick);
      }
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (frame.current) cancelAnimationFrame(frame.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, delay, perChar]);

  return (
    <span className={className} data-testid={testid} aria-label={target}>
      {out.map((c, i) => (
        <span key={i} aria-hidden className="scramble-char">
          {c === " " ? "\u00A0" : c || "\u00A0"}
        </span>
      ))}
    </span>
  );
}
